/**
 * Player-owned Theme cosmetics (Shop / Inventory).
 * Independent of Admin Game Theme (game-theme.ts).
 */
import {
  MAX_THEME_GRADIENT_COLORS,
  THEME_COSMETIC_CHANNEL_KEYS,
  THEME_COSMETIC_CHANNEL_LABELS,
  THEME_COSMETIC_EQUIP_SLOT_KEYS,
  THEME_COSMETIC_EQUIP_SLOT_LABELS,
  normalizeThemeCosmeticData,
  themeCosmeticConfiguredKeys,
  themeCosmeticEquipSlots,
  themeHasParticles,
  type ThemeCosmeticChannel,
  type ThemeCosmeticChannelKey,
  type ThemeCosmeticData,
  type ThemeCosmeticEquipSlot,
} from "../../lib/shopTypes";

export type EquippedThemeChannels = Partial<Record<ThemeCosmeticEquipSlot, string>>;

export {
  THEME_COSMETIC_CHANNEL_KEYS,
  THEME_COSMETIC_CHANNEL_LABELS,
  THEME_COSMETIC_EQUIP_SLOT_KEYS,
  THEME_COSMETIC_EQUIP_SLOT_LABELS,
  normalizeThemeCosmeticData,
  themeCosmeticConfiguredKeys,
  themeCosmeticEquipSlots,
  themeHasParticles,
  type ThemeCosmeticChannel,
  type ThemeCosmeticChannelKey,
  type ThemeCosmeticData,
  type ThemeCosmeticEquipSlot,
};

const themeDataById = new Map<string, ThemeCosmeticData>();

let equippedChannels: EquippedThemeChannels = {};
let onApplied: (() => void) | null = null;

/** Shop Creator navigable Theme preview (Home + play; records/Dots off). */
let previewData: ThemeCosmeticData | null = null;
let previewPlayActive = false;

export function setThemeCosmeticPreviewData(data: ThemeCosmeticData | null): void {
  previewData = data ? normalizeThemeCosmeticData(data) : null;
}

export function getThemeCosmeticPreviewData(): ThemeCosmeticData | null {
  return previewData;
}

export function setThemeCosmeticPreviewPlay(active: boolean): void {
  previewPlayActive = !!active;
}

/** True while Shop Creator Theme Preview is open (navigate / play without records or Dots). */
export function isThemeCosmeticPreviewPlay(): boolean {
  return previewPlayActive;
}

function resolveEffectiveChannel(key: ThemeCosmeticChannelKey): ThemeCosmeticChannel | null {
  if (previewData) return previewData[key] ?? null;
  const id = equippedChannels[key];
  if (!id) return null;
  return themeDataById.get(id)?.[key] ?? null;
}

/** Canvas ball colour from equipped / preview Theme (wins over Admin Game Theme). */
export function getPlayerThemeBallsPaint(): {
  color: string | null;
  gradientTo: string | null;
} {
  const ch = resolveEffectiveChannel("balls");
  if (!ch?.colors?.length) return { color: null, gradientTo: null };
  return {
    color: ch.colors[0],
    gradientTo:
      ch.mode === "gradient" && ch.colors.length > 1 ? ch.colors[ch.colors.length - 1] : null,
  };
}

/** True when equipped / preview Theme is driving the balls channel. */
export function hasPlayerThemeBalls(): boolean {
  return !!getPlayerThemeBallsPaint().color;
}

/** Mix helpers for button sky vars (mirrors legacy-app applyButtonGradientCssVars). */
function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const h = hex.replace("#", "");
  const n = parseInt(h.length === 3 ? h.replace(/./g, (c) => c + c) : h, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

function rgbToHex(r: number, g: number, b: number): string {
  return `#${[r, g, b].map((n) => Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, "0")).join("")}`;
}

function mixRgb(
  a: { r: number; g: number; b: number },
  b: { r: number; g: number; b: number },
  t: number,
) {
  return {
    r: a.r + (b.r - a.r) * t,
    g: a.g + (b.g - a.g) * t,
    b: a.b + (b.b - a.b) * t,
  };
}

export function registerThemeCosmeticData(id: string, data: ThemeCosmeticData | null | undefined): void {
  if (!id) return;
  const norm = normalizeThemeCosmeticData(data);
  if (norm) themeDataById.set(id, norm);
  else themeDataById.delete(id);
}

export function registerThemeCosmeticBatch(
  items: Array<{ id: string; themeData?: ThemeCosmeticData | null; category?: string }>,
): void {
  for (const item of items) {
    if (item.category === "theme" || item.themeData) {
      registerThemeCosmeticData(item.id, item.themeData);
    }
  }
}

export function getThemeCosmeticData(id: string): ThemeCosmeticData | null {
  return themeDataById.get(id) ?? null;
}

export function setEquippedThemeChannelsState(channels: EquippedThemeChannels): void {
  equippedChannels = { ...channels };
}

export function getEquippedThemeChannelsState(): EquippedThemeChannels {
  return { ...equippedChannels };
}

export function setThemeCosmeticAppliedListener(fn: (() => void) | null): void {
  onApplied = fn;
}

export function cssGradientFromChannel(ch: ThemeCosmeticChannel): string {
  const colors = ch.colors.length ? ch.colors : ["#ffffff"];
  if (ch.mode === "solid" || colors.length === 1) {
    return colors[0];
  }
  const angle = Number.isFinite(ch.angleDeg) ? Number(ch.angleDeg) : 180;
  const stops = colors
    .map((c, i) => `${c} ${(i / Math.max(1, colors.length - 1)) * 100}%`)
    .join(", ");
  return `linear-gradient(${angle}deg, ${stops})`;
}

/** Build a catalog thumbnail PNG — background channel only. */
export function buildThemeCosmeticPreviewDataUrl(data: ThemeCosmeticData): string {
  const w = 128;
  const h = 128;
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) return "";

  const keys = themeCosmeticConfiguredKeys(data);
  ctx.fillStyle = "#121218";
  ctx.fillRect(0, 0, w, h);

  const bg = data.background;
  if (bg && bg.colors.length) {
    if (bg.mode === "gradient" && bg.colors.length > 1) {
      const angle = bg.angleDeg ?? 180;
      const rad = ((angle - 90) * Math.PI) / 180;
      const x1 = w / 2 - Math.cos(rad) * w;
      const y1 = h / 2 - Math.sin(rad) * h;
      const x2 = w / 2 + Math.cos(rad) * w;
      const y2 = h / 2 + Math.sin(rad) * h;
      const g = ctx.createLinearGradient(x1, y1, x2, y2);
      bg.colors.forEach((c, i) => {
        g.addColorStop(i / Math.max(1, bg.colors.length - 1), c);
      });
      ctx.fillStyle = g;
    } else {
      ctx.fillStyle = bg.colors[0];
    }
    ctx.fillRect(0, 0, w, h);
  } else {
    ctx.fillStyle = "#1a1a22";
    ctx.fillRect(0, 0, w, h);
    const particle = data.decorations?.find((d) => d.kind === "particles");
    if (particle?.imageData) {
      // Sync thumbnail: draw a simple particles mark when no bg channel.
      ctx.fillStyle = "#66ffff";
      ctx.font = "bold 28px sans-serif";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText("✦", w / 2, h / 2);
    } else if (!keys.length) {
      ctx.strokeStyle = "#555";
      ctx.setLineDash([4, 4]);
      ctx.strokeRect(16, 16, w - 32, h - 32);
    }
  }

  return canvas.toDataURL("image/png");
}

function applyChannelSolidOrGradient(
  root: HTMLElement,
  body: HTMLElement,
  ch: ThemeCosmeticChannel,
  opts: {
    solidVar: string;
    gradientClass: string;
    gradientVar?: string;
    angleVar?: string;
    secondaryVar?: string;
    textFromVar?: string;
    textToVar?: string;
  },
): void {
  const first = ch.colors[0] || "#ffffff";
  const last = ch.colors[ch.colors.length - 1] || first;
  root.style.setProperty(opts.solidVar, first);
  if (ch.mode === "gradient" && ch.colors.length > 1) {
    body.classList.add(opts.gradientClass);
    if (opts.gradientVar) {
      root.style.setProperty(opts.gradientVar, cssGradientFromChannel(ch));
    }
    if (opts.angleVar) {
      root.style.setProperty(opts.angleVar, `${ch.angleDeg ?? 180}deg`);
    }
    if (opts.secondaryVar) {
      root.style.setProperty(opts.secondaryVar, last);
    }
    if (opts.textFromVar && opts.textToVar) {
      root.style.setProperty(opts.textFromVar, first);
      root.style.setProperty(opts.textToVar, last);
    }
  } else {
    body.classList.remove(opts.gradientClass);
    if (opts.gradientVar) root.style.removeProperty(opts.gradientVar);
    if (opts.angleVar) root.style.removeProperty(opts.angleVar);
    if (opts.secondaryVar) root.style.removeProperty(opts.secondaryVar);
    if (opts.textFromVar) root.style.removeProperty(opts.textFromVar);
    if (opts.textToVar) root.style.removeProperty(opts.textToVar);
  }
}

function clearPlayerThemeClasses(root: HTMLElement, body: HTMLElement): void {
  body.classList.remove(
    "player-theme-bg-gradient",
    "player-theme-balls-gradient",
    "player-theme-text-gradient",
    "player-theme-buttons-active",
  );
  [
    "--player-theme-bg-gradient",
    "--player-theme-bg-angle",
    "--player-theme-text-from",
    "--player-theme-text-to",
    "--player-theme-text-gradient",
    "--ball-color-to",
  ].forEach((v) => root.style.removeProperty(v));
}

/**
 * Overlay equipped Theme channels on top of Settings colours.
 * Call after applySettings(); Admin Game Theme may still override afterward.
 */
export function applyEquippedThemeCosmetics(opts?: {
  /** When Admin Theme requests restore of a channel, still apply player theme if set. */
  onlyChannels?: Partial<Record<ThemeCosmeticChannelKey, boolean>>;
}): void {
  const root = document.documentElement;
  const body = document.body;
  const only = opts?.onlyChannels;

  const shouldTouch = (key: ThemeCosmeticChannelKey) => !only || !!only[key];

  const resolve = (key: ThemeCosmeticChannelKey): ThemeCosmeticChannel | null => {
    if (!shouldTouch(key)) return null;
    return resolveEffectiveChannel(key);
  };

  if (!only) clearPlayerThemeClasses(root, body);

  const bg = resolve("background");
  if (shouldTouch("background")) {
    if (bg) {
      root.style.setProperty("--menu-bg-raw", bg.colors[0]);
      root.style.setProperty("--game-bg-raw", bg.colors[0]);
      root.style.setProperty("--menu-bg-color", bg.colors[0]);
      root.style.setProperty("--game-bg-color", bg.colors[0]);
      root.style.setProperty("--bg-color", bg.colors[0]);
      if (bg.mode === "gradient" && bg.colors.length > 1) {
        const grad = cssGradientFromChannel(bg);
        root.style.setProperty("--player-theme-bg-gradient", grad);
        root.style.setProperty("--player-theme-bg-angle", `${bg.angleDeg ?? 180}deg`);
        root.style.setProperty("--menu-bg-to", bg.colors[bg.colors.length - 1]);
        root.style.setProperty("--game-bg-to", bg.colors[bg.colors.length - 1]);
        body.classList.add("player-theme-bg-gradient");
      } else {
        root.style.removeProperty("--player-theme-bg-gradient");
        root.style.removeProperty("--player-theme-bg-angle");
        body.classList.remove("player-theme-bg-gradient");
      }
    } else if (only) {
      root.style.removeProperty("--player-theme-bg-gradient");
      root.style.removeProperty("--player-theme-bg-angle");
      body.classList.remove("player-theme-bg-gradient");
    }
  }

  const balls = resolve("balls");
  if (shouldTouch("balls")) {
    if (balls) {
      root.style.setProperty("--ball-color", balls.colors[0]);
      document.body.classList.remove("admin-theme-balls-gradient");
      if (balls.mode === "gradient" && balls.colors.length > 1) {
        root.style.setProperty("--ball-color-to", balls.colors[balls.colors.length - 1]);
        body.classList.add("player-theme-balls-gradient");
      } else {
        root.style.removeProperty("--ball-color-to");
        body.classList.remove("player-theme-balls-gradient");
      }
    } else if (only) {
      body.classList.remove("player-theme-balls-gradient");
    }
  }

  const buttons = resolve("buttons");
  if (shouldTouch("buttons")) {
    if (buttons) {
      const from = buttons.colors[0];
      const to = buttons.colors[buttons.colors.length - 1] || from;
      root.style.setProperty("--btn-main-bg", from);
      root.style.setProperty("--btn-main-glow", from);
      root.style.setProperty("--btn-green-bg", from);
      root.style.setProperty("--btn-green-glow", from);
      root.style.setProperty("--home-start-glow", from);
      body.classList.add("player-theme-buttons-active");
      if (buttons.mode === "gradient" && buttons.colors.length > 1) {
        const a = hexToRgb(from);
        const b = hexToRgb(to);
        const mid = mixRgb(a, b, 0.45);
        const edge = mixRgb(b, { r: 0, g: 0, b: 0 }, 0.35);
        root.style.setProperty("--btn-sky-light", from);
        root.style.setProperty("--btn-sky-mid", rgbToHex(mid.r, mid.g, mid.b));
        root.style.setProperty("--btn-sky-edge", rgbToHex(edge.r, edge.g, edge.b));
        root.style.setProperty("--btn-sky-edge-hover", to);
      } else {
        root.style.setProperty("--btn-sky-light", from);
        root.style.setProperty("--btn-sky-mid", from);
        root.style.setProperty("--btn-sky-edge", from);
        root.style.setProperty("--btn-sky-edge-hover", from);
      }
    } else if (only) {
      body.classList.remove("player-theme-buttons-active");
    }
  }

  const text = resolve("text");
  if (shouldTouch("text")) {
    if (text) {
      const from = text.colors[0];
      root.style.setProperty("--text-color", from);
      root.style.setProperty("--home-title-glow", from);
      if (text.mode === "gradient" && text.colors.length > 1) {
        root.style.setProperty("--player-theme-text-from", from);
        root.style.setProperty("--player-theme-text-to", text.colors[text.colors.length - 1]);
        root.style.setProperty("--player-theme-text-gradient", cssGradientFromChannel(text));
        body.classList.add("player-theme-text-gradient");
      } else {
        root.style.removeProperty("--player-theme-text-from");
        root.style.removeProperty("--player-theme-text-to");
        root.style.removeProperty("--player-theme-text-gradient");
        body.classList.remove("player-theme-text-gradient");
      }
    } else if (only) {
      root.style.removeProperty("--player-theme-text-from");
      root.style.removeProperty("--player-theme-text-to");
      root.style.removeProperty("--player-theme-text-gradient");
      body.classList.remove("player-theme-text-gradient");
    }
  }

  onApplied?.();
}

/** Empty channel editor state for Shop Creator. */
export type ThemeChannelEditorState = {
  enabled: boolean;
  mode: "solid" | "gradient";
  colors: string[];
  angleDeg: number;
};

export function emptyThemeEditorState(): Record<ThemeCosmeticChannelKey, ThemeChannelEditorState> {
  return {
    background: { enabled: false, mode: "solid", colors: ["#1a1a2e"], angleDeg: 135 },
    balls: { enabled: false, mode: "solid", colors: ["#66ffff"], angleDeg: 135 },
    buttons: { enabled: false, mode: "solid", colors: ["#66ffff"], angleDeg: 165 },
    text: { enabled: false, mode: "solid", colors: ["#ffffff"], angleDeg: 90 },
  };
}

export function themeDataFromEditorState(
  state: Record<ThemeCosmeticChannelKey, ThemeChannelEditorState>,
  decorations?: ThemeCosmeticData["decorations"],
): ThemeCosmeticData | null {
  const out: ThemeCosmeticData = {};
  for (const key of THEME_COSMETIC_CHANNEL_KEYS) {
    const s = state[key];
    if (!s.enabled || !s.colors.length) continue;
    if (s.mode === "solid") {
      out[key] = { mode: "solid", colors: [s.colors[0]] };
    } else {
      out[key] = {
        mode: "gradient",
        colors: s.colors.slice(0, MAX_THEME_GRADIENT_COLORS),
        angleDeg: ((Math.round(s.angleDeg) % 360) + 360) % 360,
      };
    }
  }
  if (decorations?.length) {
    out.decorations = decorations.filter((d) => d.kind === "particles");
  }
  return Object.keys(out).length ? out : null;
}

export function editorStateFromThemeData(
  data: ThemeCosmeticData | null | undefined,
): Record<ThemeCosmeticChannelKey, ThemeChannelEditorState> {
  const base = emptyThemeEditorState();
  if (!data) return base;
  for (const key of THEME_COSMETIC_CHANNEL_KEYS) {
    const ch = data[key];
    if (!ch) continue;
    base[key] = {
      enabled: true,
      mode: ch.mode,
      colors: [...ch.colors],
      angleDeg: ch.angleDeg ?? 180,
    };
  }
  return base;
}

/** Particle decorations from preview draft or equipped Theme cosmetics. */
export function getPlayerThemeParticleDecorations(): NonNullable<ThemeCosmeticData["decorations"]> {
  if (previewData?.decorations?.length) {
    return previewData.decorations.filter((d) => d.kind === "particles");
  }
  const byId = new Map<string, NonNullable<ThemeCosmeticData["decorations"]>[number]>();
  const particleThemeId = equippedChannels.particles;
  if (particleThemeId) {
    const data = themeDataById.get(particleThemeId);
    for (const dec of data?.decorations || []) {
      if (dec.kind === "particles") byId.set(dec.id, dec);
    }
  }
  return [...byId.values()];
}
